import React, { useMemo, useState } from 'react';
import moment, { Moment } from 'moment';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { analyticsLogger, PageType, ReportType } from 'commons/src/analytics';
import { INSIGHT_CREATED_REPORT } from 'commons/src/analytics/AnalyticsEvents';
import { mapDeviceType } from 'commons/src/commonFunctions';
import Error from 'commons/src/components/errorComponents/Error';
import PageHeader from 'commons/src/components/headers/PageHeader';
import { IntercomAPI } from 'commons/src/components/Intercom';
import MaterialIcon from 'commons/src/components/MaterialIcon';
import { dateFormats } from 'commons/src/constants';
import { AnyDeviceType, ErrorType } from 'commons/src/models/commonTypeScript';
import { fetchIaqInsightDataV2 } from '../../../actions/insightActions';
import { paths } from '../../../constants';
import { Store } from '../../../reducers';
import { BusinessRequestType as RequestType } from '../../../reducers/BusinessRequestType';
import getDeviceTypesFromReport from './getDeviceTypesFromReport';
import styles from './iaqInsight2.module.scss';
import { ReportConfigurationForm, ReportFormData } from './parts/reportForm';
import { GeneratedReportView } from './parts/reportView';

/** Maps known API error descriptions to translation keys; returns null for unknown so UI can show raw message. */
const getIaqReportErrorTranslationKey = (error: ErrorType | undefined): string | null => {
    if (!error) return null;
    const desc = error.error_description || (error as { description?: string }).description || error.message || '';
    if (typeof desc !== 'string') return null;
    if (desc.includes('No devices found for the selected spaces')) return 'IAQReport2.ReportErrorNoDevicesInPeriod';
    if (desc.includes('No spaces found for location')) return 'IAQReport2.ReportErrorNoSpacesForLocation';
    if (desc.includes('Space IDs cannot be empty')) return 'IAQReport2.ReportErrorSpaceIdsEmpty';
    if (desc.includes('No spaces found for the provided space IDs')) return 'IAQReport2.ReportErrorNoSpacesForIds';
    if (desc.includes('From date must be after to date')) return 'IAQReport2.ReportErrorFromAfterTo';
    if (desc.includes('From and to date must be within')) return 'IAQReport2.ReportErrorDateRangeTooLong';
    if (desc.includes('Location with id') && desc.includes('not found'))
        return 'IAQReport2.ReportErrorLocationNotFound';
    return null;
};

type IaqReportMetadataProps = {
    comment: string;
    onChangeComment: (value: string) => void;
};

const MetadataComment: React.FC<IaqReportMetadataProps> = ({ comment, onChangeComment }) => {
    const { t: txt } = useTranslation();
    const [isSaved, setIsSaved] = useState(false);

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
        onChangeComment(event.target.value);
        setIsSaved(false);
    };

    const handleBlur = (): void => {
        setIsSaved(true);
        setTimeout(() => setIsSaved(false), 2000);
    };

    const textareaClassName = isSaved
        ? `${styles.metadataCommentInput} ${styles.metadataCommentInputSaved}`
        : styles.metadataCommentInput;

    return (
        <div className={styles.metadataComment}>
            <textarea
                className={textareaClassName}
                rows={4}
                placeholder={txt('Insight.Comment')}
                value={comment}
                onChange={handleChange}
                onBlur={handleBlur}
            />
        </div>
    );
};

const IaqReportMetadata: React.FC<IaqReportMetadataProps> = ({ comment, onChangeComment }) => {
    const { t: txt } = useTranslation();
    const { sensorReports, thresholds, reportId } = useSelector((state: Store) => state.iaqInsight);
    const { dateFormat, userName } = useSelector((state: Store) => state.userSettings);
    const devices = useSelector((state: Store) => state.devices.devicesWithKeyInfo);

    const safeSensorReports = sensorReports || {};
    const safeThresholds = thresholds || {};

    const hasReportData = useMemo(
        () => Object.keys(safeSensorReports).length > 0 && Object.keys(safeThresholds).length > 0,
        [safeSensorReports, safeThresholds]
    );

    const deviceTypes = useMemo(
        () => getDeviceTypesFromReport(safeSensorReports, devices),
        [safeSensorReports, devices]
    );

    const formattedDeviceTypes = useMemo(() => {
        return deviceTypes.map(deviceType => {
            const mappedDeviceTypeName = mapDeviceType(deviceType as AnyDeviceType);
            const translatedFullName = txt(`${mappedDeviceTypeName}FullName`);
            return `Airthings ${translatedFullName}`;
        });
    }, [deviceTypes, txt]);

    if (!hasReportData) {
        return null;
    }

    return (
        <div className={styles.reportMetadata}>
            <div className={styles.metadataLeft}>
                <div className={styles.metadataRow}>
                    <span className={styles.metadataLabel}>{txt('Report.Generated')}:</span>
                    <span className={styles.metadataValue}>
                        {moment().format(dateFormats[dateFormat].shortFormat)}{' '}
                        {moment().format(dateFormats[dateFormat].hourFormat)}
                    </span>
                </div>
                {userName && (
                    <div className={styles.metadataRow}>
                        <span className={styles.metadataLabel}>{txt('Report.MeasurementBy')}:</span>
                        <span className={styles.metadataValue}>{userName}</span>
                    </div>
                )}
                {reportId && (
                    <div className={styles.metadataRow}>
                        <span className={styles.metadataLabel}>{txt('Report.ReportId')}:</span>
                        <span className={styles.metadataValue}>{reportId}</span>
                    </div>
                )}
                <div className={styles.metadataRow}>
                    <span className={styles.metadataLabel}>{txt('Insight.DeviceTypesInMeasurement')}:</span>
                    <span className={styles.metadataValue}>{formattedDeviceTypes.join(', ')}</span>
                </div>
            </div>
            <MetadataComment comment={comment} onChangeComment={onChangeComment} />
        </div>
    );
};

const IAQReportPage: React.FC = () => {
    const { t: txt } = useTranslation();
    const dispatch = useDispatch();

    const locationsError = useSelector((state: Store) => state.locations.error);
    const fetchReportDataLoading = useSelector(
        (state: Store) => state.requests[RequestType.FetchIaqInsightData]?.loading ?? false
    );
    const error = useSelector((state: Store) => state.requests[RequestType.FetchIaqInsightData]?.error);

    const [reportStartTime, setReportStartTime] = useState<Moment | null>(null);
    const [reportEndTime, setReportEndTime] = useState<Moment | null>(null);
    const [displayResultSection, setDisplayResultSection] = useState(false);
    const [comment, setComment] = useState('');

    if (locationsError) {
        return <Error />;
    }

    const fetchReportData = (formData: ReportFormData): void => {
        analyticsLogger(INSIGHT_CREATED_REPORT, {
            pageType: PageType.Insight,
            reportType: ReportType.IAQInsight,
            reportData: formData,
        });

        IntercomAPI('trackEvent', 'generated-IAQ-insight-report-v2', {
            locationId: formData.locationId,
            from: formData.from,
            to: formData.to,
            spaces: formData.spaces,
            sensors: formData.sensors,
        });

        dispatch(fetchIaqInsightDataV2(formData));
        setDisplayResultSection(true);
    };

    const resultContent =
        displayResultSection && !error && reportStartTime && reportEndTime ? (
            <>
                <GeneratedReportView from={reportStartTime} to={reportEndTime} comment={comment || ''} />
                <IaqReportMetadata comment={comment} onChangeComment={setComment} />
            </>
        ) : undefined;

    const translationKey = getIaqReportErrorTranslationKey(error);
    const rawMessage =
        error?.error_description || (error as { description?: string } | undefined)?.description || error?.message;
    const fallbackMessage =
        typeof rawMessage === 'string' && rawMessage ? rawMessage : txt('IAQReport2.ReportErrorGeneric');
    const messageText = translationKey ? txt(translationKey) : fallbackMessage;

    const errorPanelContent =
        displayResultSection && error ? (
            <div className={styles.reportInfoPanel}>
                <div className={styles.reportInfoPanelIcon}>
                    <MaterialIcon name="info" />
                </div>
                <p className={`${styles.placeholderText} ${styles.reportInfoPanelTitleSpacing}`}>
                    {txt('IAQReport2.ReportInfoTitle')}
                </p>
                <p className={styles.placeholderText}>{messageText}</p>
            </div>
        ) : undefined;

    const rightPanelContent = resultContent ?? errorPanelContent;

    return (
        <>
            <PageHeader title={txt('Insight.IaqAnalysis2.0')} />
            <div className={styles.pageWrapper}>
                <div className={styles.navigationBar}>
                    <Link to={`/${paths.reports}`} className={styles.backLink}>
                        <MaterialIcon name="chevron_left" />
                        <span>{txt('IAQReport2.AllReports')}</span>
                    </Link>
                    <Link to={`/${paths.iaqInsight}`} className={styles.switchLink}>
                        <MaterialIcon name="visibility" />
                        <span>{txt('IAQReport2.SwitchToOldView')}</span>
                    </Link>
                </div>
                <ReportConfigurationForm
                    onGenerateReport={fetchReportData}
                    loading={fetchReportDataLoading}
                    reportStartTime={reportStartTime}
                    reportEndTime={reportEndTime}
                    setReportStartTime={setReportStartTime}
                    setReportEndTime={setReportEndTime}
                    rightPanelContent={rightPanelContent}
                />
            </div>
        </>
    );
};

export default IAQReportPage;
